import React, { useEffect, useState } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import { isValidE164, E164_VALIDATION_MESSAGE } from "utils/phoneE164";
import LockIcon from "@mui/icons-material/Lock";
import { getDefaultKycPrograms } from "service/api";
import {
  Box,
  Card,
  CardContent,
  Checkbox,
  Container,
  FormControl,
  FormControlLabel,
  FormGroup,
  FormLabel,
  Grid,
  InputLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  SelectChangeEvent,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import CountryAutocomplete from "components/country-autocomplete";
import { motion } from "framer-motion";
import { LoadingButton } from "@mui/lab";
import { MuiTelInput, MuiTelInputCountry } from "mui-tel-input";
import BrandingLogo from "components/branding-logo";
import { useSite } from "hooks/use-site";
import { useAppDispatch } from "store";
import { setNewUser } from "features/register/registerSlice";
import { useNavigate } from "react-router-dom";
import { differenceInYears } from "date-fns";
import { errorNotification } from "../../features/notifications/notificationsSlice";
import { useTranslation } from "react-i18next";
import { useGetExcludedCountryCodesQuery } from "features/counteris/countriesApi";
import {
  yupCity,
  yupState,
  yupStreet,
  yupPostalCode,
  requiresState,
} from "utils/address";

type InitialValues = {
  first_name: string;
  last_name: string;
  email: string;
  kyc_program_id: number | null;
  phone: string;
  country: string;
  citizenship_country: string;
  user_birth_date: string;
  gender: string;
  currency: string;
  city: string;
  state: string;
  zip_code: string;
  address: string;
  isDirector: boolean;
  isUBO: boolean;
  isAuthRep: boolean;
};

interface WalletProgram {
  id: number;
  wallet_type: "Private Wallet" | "Business Wallet";
  default_currency: string;
  disable_address_fields_in_registration_form: number;
  enable_questionnaire_individual_lvl1: number;
  enable_questionnaire_corporate_lvl1: number;
  registration_allow_only_latin: number;
  max_age_in_registration: number;
  default_mobile_country_code: string;
  hide_currencies_in_registration: number;
}

const SignUpWithReferral = () => {
  const { multiCompanyMode } = useSite();
  const [walletPrograms, setWalletPrograms] = useState<WalletProgram[]>([]);
  const [chosenProgram, setChosenProgram] = useState<string>("Private Wallet");
  const [requireOnlyLatin, setRequireOnlyLatin] = useState(false);
  const [maxAge, setMaxAge] = useState<number>(99);
  const [defaultCountry, setDefaultCountry] =
    useState<MuiTelInputCountry>("CZ");
  const [hideCurrencies, setHideCurrencies] = useState(false);
  const { t } = useTranslation();

  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [currentKycProgramId, setCurrentKycProgramId] = useState<number | null>(
    null
  );
  const { data: excludedCodesData } = useGetExcludedCountryCodesQuery(
    { kycProgramId: currentKycProgramId! },
    { skip: !currentKycProgramId }
  );
  const excludedCountryCodes = (excludedCodesData?.excludedCountryCodes || [
    "IR",
  ]) as MuiTelInputCountry[];

  const onSubmit = (values: InitialValues) => {
    localStorage.removeItem("form3_json");
    if (walletPrograms.length === 1) {
      dispatch(
        setNewUser({
          password: null,
          ...values,
          form1_json: null,
          form3_json: null,
          form4_json: null,
        })
      );

      const firstProgram = walletPrograms[0];
      if (firstProgram.wallet_type === "Private Wallet") {
        if (firstProgram.enable_questionnaire_individual_lvl1 === 1) {
          navigate("/form-first");
        } else {
          navigate("/sign-up");
        }
      } else {
        if (firstProgram.enable_questionnaire_corporate_lvl1 === 1) {
          navigate("/corp-info-1");
        } else {
          navigate("/sign-up");
        }
      }
    } else if (walletPrograms.length === 2) {
      const firstProgram = walletPrograms.find(
        (program) => program.wallet_type === chosenProgram
      );

      if (!firstProgram) {
        dispatch(
          errorNotification(
            t("auth.signup_with_referral.validation.wallet_type_required")
          )
        );
        return;
      }

      const updatedValues = {
        ...values,
        kyc_program_id: firstProgram.id,
        currency: firstProgram.default_currency,
      };

      setDisableAddressFieldsInRegistrationForm(
        firstProgram.disable_address_fields_in_registration_form === 1
      );
      dispatch(
        setNewUser({
          password: null,
          ...updatedValues,
          form1_json: null,
          form3_json: null,
          form4_json: null,
        })
      );
      if (chosenProgram === "Private Wallet") {
        if (firstProgram.enable_questionnaire_individual_lvl1 === 1) {
          navigate("/form-first");
        } else {
          navigate("/sign-up");
        }
      } else if (chosenProgram === "Business Wallet") {
        if (firstProgram.enable_questionnaire_corporate_lvl1 === 1) {
          navigate("/corp-info-1");
        } else {
          navigate("/sign-up");
        }
      } else {
        dispatch(
          errorNotification(
            t("auth.signup_with_referral.validation.wallet_type_required")
          )
        );
        return;
      }
    }
  };

  const initialValues: InitialValues = {
    first_name: "",
    last_name: "",
    email: "",
    kyc_program_id: null,
    phone: "",
    user_birth_date: "",
    country: "",
    citizenship_country: "",
    gender: "",
    currency: "eur",
    city: "",
    state: "",
    zip_code: "",
    address: "",
    isDirector: false,
    isUBO: false,
    isAuthRep: false,
  };

  const [
    disableAddressFieldsInRegistrationForm,
    setDisableAddressFieldsInRegistrationForm,
  ] = useState<boolean>(false);

  const validationSchema = Yup.object().shape({
    first_name: Yup.string()
      .required(t("auth.signup_with_referral.validation.first_name_required"))
      .when([], {
        is: () => requireOnlyLatin,
        then: (schema) =>
          schema.matches(
            /^[A-Za-z\s'-]+$/,
            t("auth.signup_with_referral.validation.only_latin")
          ),
      }),

    last_name: Yup.string()
      .required(t("auth.signup_with_referral.validation.last_name_required"))
      .when([], {
        is: () => requireOnlyLatin,
        then: (schema) =>
          schema.matches(
            /^[A-Za-z\s'-]+$/,
            t("auth.signup_with_referral.validation.only_latin")
          ),
      }),
    email: Yup.string()
      .email(t("auth.signup_with_referral.validation.email_invalid"))
      .required(t("auth.signup_with_referral.validation.email_required")),
    kyc_program_id: Yup.number()
      .nullable(false)
      .required(t("auth.signup_with_referral.validation.wallet_type_required")),
    phone: Yup.string()
      .required(t("auth.signup_with_referral.validation.phone_required"))
      .test("e164", E164_VALIDATION_MESSAGE, (value) => isValidE164(value)),
    country: Yup.string().required(
      t("auth.signup_with_referral.validation.residence_country_required")
    ),
    currency: Yup.string().required(
      t("auth.signup_with_referral.validation.currency_required")
    ),
    user_birth_date: Yup.string()
      .required(t("auth.signup_with_referral.validation.birth_date_required"))
      .test(
        "user_birth_date",
        t("auth.signup_with_referral.validation.birth_date_invalid", {
          max: maxAge,
        }),
        (value) => {
          if (value) {
            const diff = differenceInYears(new Date(), new Date(value));
            return diff >= 18 && diff <= maxAge;
          }
          return false;
        }
      ),
    gender: Yup.string().required(
      t("auth.signup_with_referral.validation.gender_required")
    ),

    citizenship_country: Yup.string().when({
      is: () => !disableAddressFieldsInRegistrationForm,
      then: Yup.string().required(
        t("auth.signup_with_referral.validation.citizenship_required")
      ),
    }),
    state: Yup.string().when(
      ["country"],
      (country: string, schema: Yup.StringSchema) => {
        if (disableAddressFieldsInRegistrationForm) return schema;
        return yupState(t, requiresState(country));
      }
    ),
    city: Yup.string().when({
      is: () => !disableAddressFieldsInRegistrationForm,
      then: yupCity(t),
    }),
    address: Yup.string().when({
      is: () => !disableAddressFieldsInRegistrationForm,
      then: yupStreet(t),
    }),
    zip_code: Yup.string().when({
      is: () => !disableAddressFieldsInRegistrationForm,
      then: yupPostalCode(t),
    }),
    isDirector: Yup.boolean(),
    isUBO: Yup.boolean(),
  });

  const {
    handleSubmit,
    getFieldMeta,
    setFieldValue,
    handleChange,
    handleBlur,
    isSubmitting,
    values,
  } = useFormik<InitialValues>({
    initialValues,
    onSubmit,
    validationSchema,
  });

  useEffect(() => {
    localStorage.removeItem("managementStructure");
    localStorage.removeItem("ownershipStructure");
    getDefaultKycPrograms().then((data) => {
      setWalletPrograms(data.walletPrograms);
      const firstProgram = data.walletPrograms[0];
      setFieldValue("kyc_program_id", firstProgram.id);
      setCurrentKycProgramId(firstProgram.id);
      setFieldValue("currency", firstProgram.default_currency);
      setDisableAddressFieldsInRegistrationForm(
        firstProgram.disable_address_fields_in_registration_form === 1
      );
      setRequireOnlyLatin(firstProgram.registration_allow_only_latin === 1);
      if (firstProgram.max_age_in_registration) {
        setMaxAge(firstProgram.max_age_in_registration);
      }
      if (firstProgram.default_mobile_country_code) {
        setDefaultCountry(
          firstProgram.default_mobile_country_code as MuiTelInputCountry
        );
      }
      setHideCurrencies(firstProgram.hide_currencies_in_registration === 1);
    });
  }, [setFieldValue]);

  // Update currentKycProgramId when form value changes
  useEffect(() => {
    if (values.kyc_program_id !== null) {
      setCurrentKycProgramId(values.kyc_program_id);
    }
  }, [values.kyc_program_id]);

  const handlePhoneChange = (newValue: string) => {
    /*
    matchIsValidTel(newValue, {
      onlyCountryies: [], // optional,
      excludedCountryies: [], // optional
      continents: [] // optional
    }) // true | false
     */
    setFieldValue("phone", newValue);
  };

  const handleGenderChange = (event: { target: { value: string } }) => {
    setFieldValue("gender", event.target.value);
  };

  const handleCurrencyChange = (event: SelectChangeEvent) => {
    const {
      target: { value },
    } = event;
    setFieldValue("currency", value);
  };

  useEffect(() => {
    const personal = {
      fullName: `${values.first_name} ${values.last_name}`,
      email: values.email,
      phone: values.phone,
      country: values.country,
      address: values.address,
      zip: values.zip_code,
      city: values.city,
      state: values.state,
      nationality: values.citizenship_country,
      date_of_birth: values.user_birth_date,
    };

    if (values.isDirector) {
      localStorage.setItem("managementStructure", JSON.stringify([personal]));
    }
    if (values.isUBO) {
      localStorage.setItem(
        "ownershipStructure",
        JSON.stringify([{ ...personal, ownershipPercentage: 100 }])
      );
    }
  }, [
    values.isDirector,
    values.isUBO,
    values.first_name,
    values.last_name,
    values.email,
    values.phone,
    values.country,
    values.address,
    values.zip_code,
    values.city,
    values.state,
    values.citizenship_country,
    values.user_birth_date,
  ]);

  return (
    <Box
      display={"flex"}
      alignItems="center"
      justifyContent="center"
      sx={{ mt: 4 }}
    >
      <Container maxWidth="sm">
        <form onSubmit={handleSubmit}>
          <Card elevation={24} component={motion.div} layoutId="auth-card">
            <Box paddingY={2.5} paddingX={4} sx={{ bgcolor: "brand.auth" }}>
              <BrandingLogo width={200} />
            </Box>
            <CardContent>
              <Stack spacing={2}>
                <Box display="flex" flexDirection="row" alignItems="center">
                  <LockIcon fontSize="small" />
                  <Typography variant="h6" fontWeight={"600"}>
                    {t("auth.signup_with_referral.title")}
                  </Typography>
                </Box>
                <Grid container rowSpacing={2} columnSpacing={0.5}>
                  {walletPrograms.length === 2 && (
                    <Grid xs={12} item>
                      <FormControl fullWidth>
                        <InputLabel id="wallet-type-label">
                          {t("auth.signup_with_referral.wallet_type")}
                        </InputLabel>
                        <Select
                          labelId="wallet-type-label"
                          id="walletType"
                          value={chosenProgram}
                          onChange={(e) => {
                            const selectedType = e.target.value;
                            setChosenProgram(selectedType);
                            const selectedProgram = walletPrograms.find(
                              (program) => program.wallet_type === selectedType
                            );
                            if (selectedProgram) {
                              setFieldValue(
                                "kyc_program_id",
                                selectedProgram.id
                              );
                              setFieldValue(
                                "currency",
                                selectedProgram.default_currency
                              );
                              setDisableAddressFieldsInRegistrationForm(
                                selectedProgram.disable_address_fields_in_registration_form ===
                                  1
                              );
                              setRequireOnlyLatin(
                                selectedProgram.registration_allow_only_latin ===
                                  1
                              );
                              if (selectedProgram.max_age_in_registration) {
                                setMaxAge(
                                  selectedProgram.max_age_in_registration
                                );
                              }
                              if (selectedProgram.default_mobile_country_code) {
                                setDefaultCountry(
                                  selectedProgram.default_mobile_country_code as MuiTelInputCountry
                                );
                              }
                              setHideCurrencies(
                                selectedProgram.hide_currencies_in_registration ===
                                  1
                              );
                            }
                          }}
                          label="Wallet Type"
                        >
                          <MenuItem value="Business Wallet">
                            {t(
                              "auth.signup_with_referral.wallet_type_business"
                            )}
                          </MenuItem>
                          <MenuItem value="Private Wallet">
                            {t("auth.signup_with_referral.wallet_type_private")}
                          </MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>
                  )}
                  <Grid xs={12} item>
                    <TextField
                      label={t(
                        "auth.signup_with_referral.applicant_first_name"
                      )}
                      name="first_name"
                      variant="outlined"
                      value={getFieldMeta("first_name").value}
                      fullWidth
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={
                        getFieldMeta("first_name").touched &&
                        !!getFieldMeta("first_name").error
                      }
                      helperText={
                        getFieldMeta("first_name").touched &&
                        getFieldMeta("first_name").error
                      }
                    />
                  </Grid>
                  <Grid xs={12} item>
                    <TextField
                      label={t("auth.signup_with_referral.applicant_last_name")}
                      name="last_name"
                      variant="outlined"
                      value={getFieldMeta("last_name").value}
                      fullWidth
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={
                        getFieldMeta("last_name").touched &&
                        !!getFieldMeta("last_name").error
                      }
                      helperText={
                        getFieldMeta("last_name").touched &&
                        getFieldMeta("last_name").error
                      }
                    />
                  </Grid>
                  {multiCompanyMode && chosenProgram === "Business Wallet" && (
                    <Grid item xs={12}>
                      <FormControl component="fieldset" fullWidth>
                        <FormLabel component="legend">
                          Please select your position within the company?
                        </FormLabel>
                        <FormGroup row>
                          <FormControlLabel
                            control={
                              <Checkbox
                                name="isDirector"
                                checked={values.isDirector}
                                onChange={handleChange}
                              />
                            }
                            label="Director"
                          />
                          <FormControlLabel
                            control={
                              <Checkbox
                                name="isUBO"
                                checked={values.isUBO}
                                onChange={handleChange}
                              />
                            }
                            label="Ultimate Beneficial Owner"
                          />
                          <FormControlLabel
                            control={
                              <Checkbox
                                name="isAuthRep"
                                checked={values.isAuthRep}
                                onChange={handleChange}
                              />
                            }
                            label="Authorized Representative"
                          />
                        </FormGroup>
                      </FormControl>
                    </Grid>
                  )}
                  <Grid xs={12} item>
                    <TextField
                      type="date"
                      label={t("auth.signup_with_referral.dob")}
                      name="user_birth_date"
                      variant="outlined"
                      InputLabelProps={{ shrink: true }}
                      value={getFieldMeta("user_birth_date").value}
                      fullWidth
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={
                        getFieldMeta("user_birth_date").touched &&
                        !!getFieldMeta("user_birth_date").error
                      }
                      helperText={
                        getFieldMeta("user_birth_date").touched &&
                        getFieldMeta("user_birth_date").error
                      }
                    />
                  </Grid>
                  <Grid xs={12} item>
                    <FormControl>
                      <FormLabel id="demo-radio-buttons-group-label">
                        {t("auth.signup_with_referral.gender")}
                      </FormLabel>
                      <RadioGroup name="gender" onChange={handleGenderChange}>
                        <Box display="flex" flexDirection="row">
                          <FormControlLabel
                            value="0"
                            control={<Radio />}
                            label={t("auth.signup_with_referral.female")}
                          />
                          <FormControlLabel
                            value="1"
                            control={<Radio />}
                            label={t("auth.signup_with_referral.male")}
                          />
                        </Box>
                      </RadioGroup>
                      {getFieldMeta("gender").touched &&
                        getFieldMeta("gender").error && (
                          <React.Fragment>
                            <Typography
                              component="span"
                              sx={{ color: "error.main" }}
                            >
                              {getFieldMeta("gender").error}
                            </Typography>
                          </React.Fragment>
                        )}
                    </FormControl>
                  </Grid>
                  <Grid xs={12} item>
                    <TextField
                      label={t("auth.signup_with_referral.email")}
                      name="email"
                      sx={{ flexShrink: 1 }}
                      variant="outlined"
                      value={getFieldMeta("email").value}
                      fullWidth
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={
                        getFieldMeta("email").touched &&
                        !!getFieldMeta("email").error
                      }
                      helperText={
                        getFieldMeta("email").touched &&
                        getFieldMeta("email").error
                      }
                    />
                  </Grid>
                  <Grid xs={12} item>
                    <MuiTelInput
                      excludedCountries={excludedCountryCodes}
                      focusOnSelectCountry
                      forceCallingCode
                      fullWidth
                      key={defaultCountry}
                      defaultCountry={defaultCountry}
                      label={t("auth.signup_with_referral.mobile")}
                      name="phone"
                      value={getFieldMeta("phone").value}
                      onChange={handlePhoneChange}
                      error={
                        getFieldMeta("phone").touched &&
                        !!getFieldMeta("phone").error
                      }
                      helperText={
                        getFieldMeta("phone").touched &&
                        getFieldMeta("phone").error
                      }
                    />
                    <React.Fragment>
                      <Typography variant="body2" color="textSecondary">
                        {t("auth.signup_with_referral.mobile_hint")}
                      </Typography>
                    </React.Fragment>
                  </Grid>

                  <Grid xs={12} item>
                    <CountryAutocomplete
                      label={t("auth.signup_with_referral.residence_country")}
                      onBlur={handleBlur}
                      name="country"
                      value={getFieldMeta("country").value}
                      onChange={handleChange}
                      kycProgramId={values.kyc_program_id}
                      error={
                        getFieldMeta("country").touched
                          ? getFieldMeta("country").error
                          : undefined
                      }
                    />
                  </Grid>
                  {!disableAddressFieldsInRegistrationForm && (
                    <>
                      <Grid xs={12} item>
                        <CountryAutocomplete
                          label={t(
                            "auth.signup_with_referral.citizenship_country"
                          )}
                          onBlur={handleBlur}
                          name="citizenship_country"
                          value={getFieldMeta("citizenship_country").value}
                          onChange={handleChange}
                          kycProgramId={values.kyc_program_id}
                          error={
                            getFieldMeta("citizenship_country").touched
                              ? getFieldMeta("citizenship_country").error
                              : undefined
                          }
                        />
                      </Grid>
                      <Grid xs={12} item>
                        <TextField
                          label={t("auth.signup_with_referral.state")}
                          name="state"
                          sx={{ flexShrink: 1 }}
                          variant="outlined"
                          value={getFieldMeta("state").value}
                          fullWidth
                          onChange={handleChange}
                          onBlur={handleBlur}
                          error={
                            getFieldMeta("state").touched &&
                            !!getFieldMeta("state").error
                          }
                          helperText={
                            getFieldMeta("state").touched &&
                            getFieldMeta("state").error
                          }
                        />
                      </Grid>
                      <Grid xs={12} item>
                        <TextField
                          label={t("auth.signup_with_referral.city")}
                          name="city"
                          sx={{ flexShrink: 1 }}
                          variant="outlined"
                          value={getFieldMeta("city").value}
                          fullWidth
                          onChange={handleChange}
                          onBlur={handleBlur}
                          error={
                            getFieldMeta("city").touched &&
                            !!getFieldMeta("city").error
                          }
                          helperText={
                            getFieldMeta("city").touched &&
                            getFieldMeta("city").error
                          }
                        />
                      </Grid>
                      <Grid xs={12} item>
                        <TextField
                          label={t("auth.signup_with_referral.address")}
                          name="address"
                          sx={{ flexShrink: 1 }}
                          variant="outlined"
                          value={getFieldMeta("address").value}
                          fullWidth
                          onChange={handleChange}
                          onBlur={handleBlur}
                          error={
                            getFieldMeta("address").touched &&
                            !!getFieldMeta("address").error
                          }
                          helperText={
                            getFieldMeta("address").touched &&
                            getFieldMeta("address").error
                          }
                        />
                      </Grid>
                      <Grid xs={12} item>
                        <TextField
                          label={t("auth.signup_with_referral.zip_code")}
                          name="zip_code"
                          sx={{ flexShrink: 1 }}
                          variant="outlined"
                          value={getFieldMeta("zip_code").value}
                          fullWidth
                          onChange={handleChange}
                          onBlur={handleBlur}
                          error={
                            getFieldMeta("zip_code").touched &&
                            !!getFieldMeta("zip_code").error
                          }
                          helperText={
                            getFieldMeta("zip_code").touched &&
                            getFieldMeta("zip_code").error
                          }
                        />
                      </Grid>
                    </>
                  )}
                  {!hideCurrencies && (
                    <Grid xs={12} item>
                      <FormControl fullWidth id="currency">
                        <InputLabel>
                          {t("auth.signup_with_referral.currency")}
                        </InputLabel>
                        <Select
                          name="currency"
                          label={t("auth.signup_with_referral.currency")}
                          value={values.currency}
                          onChange={handleCurrencyChange}
                          onBlur={handleBlur}
                        >
                          <MenuItem value={"aud"}>AUD</MenuItem>
                          <MenuItem value={"brl"}>BRL</MenuItem>
                          <MenuItem value={"cad"}>CAD</MenuItem>
                          <MenuItem value={"chf"}>CHF</MenuItem>
                          <MenuItem value={"eur"}>EUR</MenuItem>
                          <MenuItem value={"gbp"}>GBP</MenuItem>
                          <MenuItem value={"mxn"}>MXN</MenuItem>
                          <MenuItem value={"nzd"}>NZD</MenuItem>
                          <MenuItem value={"sek"}>SEK</MenuItem>
                          <MenuItem value={"usd"}>USD</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>
                  )}
                </Grid>
                <Stack spacing={2}>
                  <Box
                    display="flex"
                    flexDirection="column"
                    gap={2}
                    alignItems="center"
                  >
                    <LoadingButton
                      fullWidth
                      loading={isSubmitting}
                      variant="contained"
                      type="submit"
                    >
                      {t("auth.signup_with_referral.next_step")}
                    </LoadingButton>
                    <LoadingButton
                      color="error"
                      onClick={() => navigate("/signin")}
                      fullWidth
                      variant="contained"
                    >
                      {t("auth.signup_with_referral.cancel")}
                    </LoadingButton>
                  </Box>
                </Stack>
              </Stack>
            </CardContent>
          </Card>
        </form>
      </Container>
    </Box>
  );
};
export default SignUpWithReferral;
